import React, { useCallback, useEffect, useRef } from 'react';
import { Badge } from 'antd';
import { useIntl } from 'react-intl';
import { BellWrap } from './styles';
import { NotificationsBellProps } from './types';
import { useRealtimeFarmNotifications } from 'realtime/useRealtimeFarmNotifications';
import useQueryApiClient from 'utils/useQueryApiClient';

export function NotificationsBell({
    farmId,
    onClick,
    onRealtime,
    onRefetchCount,
    showZero = false,
    overflowCount = 99,
  }: NotificationsBellProps) {
  const intl = useIntl();

  const { data, refetch } = useQueryApiClient({
    request: {
      url: '/api/v2/notifications/unread-count',
      data: { farm_id: farmId },
    },
  });

  const count =
    typeof data?.data?.count === 'number'
      ? data.data.count
      : typeof data?.count === 'number'
      ? data.count
      : 0;

  // Refetch when farmId changes (e.g., when user data loads)
  const prevFarmIdRef = useRef(farmId);
  useEffect(() => {
    if (farmId && farmId !== prevFarmIdRef.current) {
      prevFarmIdRef.current = farmId;
      refetch();
    }
  }, [farmId, refetch]);

  const onRealtimeRef = useRef(onRealtime);
  useEffect(() => {
    onRealtimeRef.current = onRealtime;
  }, [onRealtime]);

  const handleRealtimeEvent = useCallback(
    (payload: any) => {
      onRealtimeRef.current?.(payload);
      refetch();
    },
    [refetch]
  );

  // Subscribe to new notifications
  useRealtimeFarmNotifications({
    farmId,
    eventName: '.farm.notification',
    onEvent: handleRealtimeEvent,
  });

  // Subscribe to notification updates (read, archived, etc.)
  useRealtimeFarmNotifications({
    farmId,
    eventName: '.notification.updated',
    onEvent: handleRealtimeEvent,
  });

  useEffect(() => {
    onRefetchCount?.(refetch);
  }, [onRefetchCount]);

  const localizedTitle = intl.formatMessage({ id: 'notifications.bell.title' });

  return (
    <BellWrap>
      <button
        type="button"
        onClick={onClick}
        aria-label={localizedTitle}
        title={localizedTitle}
        style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
      >
        <Badge count={count} showZero={showZero} overflowCount={overflowCount}>
          <img src="/svgs/admin/notifications-bell.svg" width={22} height={22} alt="" aria-hidden />
        </Badge>
      </button>
    </BellWrap>
  );
}
